/**
 * getAdminAuthToken — V2.N.2
 *
 * Retourne le bon Bearer token pour les requêtes vers /api/admin/* :
 *
 *   1. Si une session Supabase est active ET le user est admin
 *      (champ user_profiles.role === 'admin'), on renvoie son
 *      access_token JWT. C'est le mode recommandé.
 *
 *   2. Sinon, on tombe sur la admin_key legacy stockée dans
 *      localStorage (pendant la transition V2.N).
 *
 *   3. Si aucune des deux : null. L'appelant doit alors gérer
 *      l'absence de token (rediriger vers le formulaire de login admin).
 *
 * Cette fonction est asynchrone parce que supabase.auth.getSession()
 * retourne une Promise. Conséquence : les call sites doivent await.
 */

import { supabase } from '../../lib/supabase/client';
import { StorageService } from '../../services/storage';

export async function getAdminAuthToken(): Promise<string | null> {
  // Mode 1 : JWT Supabase
  try {
    const { data } = await supabase.auth.getSession();
    const session = data?.session;
    if (session?.access_token && session.user?.id) {
      // On ne fait PAS le check role ici (coût d'un round-trip). Le
      // serveur le fera de toute façon via verifyAdmin. On retourne
      // le JWT optimistement.
      return session.access_token;
    }
  } catch {
    // ignore
  }

  // Mode 2 : admin_key legacy
  const adminKey = StorageService.get<string>('admin_key', '');
  if (adminKey) return adminKey;

  return null;
}

/**
 * Helper qui construit directement l'Authorization header pour fetch().
 * Renvoie un objet vide si aucun token — la requête échouera donc en
 * 401 et l'appelant pourra rediriger.
 */
export async function adminAuthHeader(): Promise<Record<string, string>> {
  const token = await getAdminAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
